/**
 * 项目时区 offset 的单一解析入口。
 *
 * 该模块保持纯函数，避免 `project-timezone` / 组件层重复维护 UTC offset 正则；
 * 展示组件只消费结构化结果，日期服务只消费 totalMinutes，从而让解析规则变更只影响这一处。
 */
const TIMEZONE_OFFSET_REGEX = /^UTC(?:([+-])(\d{1,2})(?::(\d{2}))?)?$/;

export type ProjectTimezoneRelation = 'same' | 'ahead' | 'behind';

export interface ProjectTimezoneOffset {
  sign: '+' | '-';
  hours: number;
  minutes: number;
  totalMinutes: number;
}

interface ProjectTimezoneOffsetInfoOptions {
  projectTimezone: string;
  localOffsetMinutes: number;
}

export interface ProjectTimezoneOffsetInfo {
  projectOffsetMinutes: number;
  localOffsetMinutes: number;
  deltaMinutes: number;
  relation: ProjectTimezoneRelation;
}

export const parseProjectTimezoneOffset = (timezone: string): ProjectTimezoneOffset | null => {
  const match = TIMEZONE_OFFSET_REGEX.exec(timezone.trim());
  if (!match) {
    return null;
  }
  if (!match[1]) {
    return {
      sign: '+',
      hours: 0,
      minutes: 0,
      totalMinutes: 0,
    };
  }

  const sign = match[1] === '+' ? '+' : '-';
  const hours = Number(match[2]);
  const minutes = Number(match[3] ?? '0');
  const unsignedMinutes = hours * 60 + minutes;
  const totalMinutes = sign === '+' ? unsignedMinutes : -unsignedMinutes;

  return {
    sign,
    hours,
    minutes,
    // `UTC-0` 与 `UTC+0` 在业务语义上都是零偏移，禁止把 JavaScript 的 `-0` 泄漏给调用方。
    totalMinutes: totalMinutes === 0 ? 0 : totalMinutes,
  };
};

export const parseProjectTimezoneOffsetMinutes = (timezone: string) => (
  parseProjectTimezoneOffset(timezone)?.totalMinutes ?? null
);

export const getProjectTimezoneOffsetInfo = ({
  projectTimezone,
  localOffsetMinutes,
}: ProjectTimezoneOffsetInfoOptions): ProjectTimezoneOffsetInfo => {
  const projectOffsetMinutes = parseProjectTimezoneOffsetMinutes(projectTimezone);
  if (projectOffsetMinutes === null) {
    throw new Error(`Invalid project timezone: ${projectTimezone}`);
  }
  const deltaMinutes = projectOffsetMinutes - localOffsetMinutes;
  let relation: ProjectTimezoneRelation = 'same';
  // 时区相对关系属于业务规则；组件层只根据 relation 选择文案，不再自行比较 offset。
  if (deltaMinutes > 0) {
    relation = 'ahead';
  }
  if (deltaMinutes < 0) {
    relation = 'behind';
  }

  return {
    projectOffsetMinutes,
    localOffsetMinutes,
    deltaMinutes,
    relation,
  };
};
